<template>
  <div class="f-radios-wrap" ref="multipleWrapper">
    <div class="option-wrapper" role="listbox">
        <div
          v-for="(option, index) in question.options"
          v-on:click.prevent="toggleAnswer(option)"
          :key="'m' + index"
          class="choice-option"
          v-bind:class="{ 'choice-option-selected': option.selected }"
          role="option"
        >
          <input
            :checked="option.selected"
            :value="option.value"
            :disabled="disableAnswer"
            type="checkbox"
            class="checkbox"
          />
          <div v-if="option.file" class="choice-image">
            <img :src="buildImageLink(option.file.key)"/>
          </div>
          <div v-else class="choice-label">
            <span>{{ option.value }}</span>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import BaseType from "./BaseType.vue";
import { QuestionType } from "../../models/QuestionModel";

const bucketName = process.env.VUE_APP_BUCKET_NAME;
const region = process.env.VUE_APP_AWS_REGION;

export default {
  extends: BaseType,
  name: QuestionType.MultipleChoiceQuiz,

  data() {
    return {
      editingOther: false
    };
  },

  mounted() {
    if (this.question.answerOptions === 'multiple') {
      this.dataValue = [];
    }

    this.addKeyListener();
  },

  beforeDestroy() {
    this.removeKeyListener();
  },

  watch: {
    active(value) {
      if (value) {
        this.addKeyListener();

        if (this.question.multiple && this.question.answered) {
          this.enterPressed = false;
        }
      } else {
        this.removeKeyListener();
      }
    }
  },

  methods: {
    addKeyListener() {
      this.removeKeyListener();
      document.addEventListener("keyup", this.onKeyListener);
    },

    removeKeyListener() {
      document.removeEventListener("keyup", this.onKeyListener);
    },

    /**
     * Listens for keyboard events (A, B, C, ...)
     */
    onKeyListener($event) {
      if (
        this.active &&
        !this.editingOther &&
        $event.key &&
        $event.key.length === 1
      ) {
        let keyCode = $event.key.toUpperCase().charCodeAt(0);

        if (keyCode >= 65 && keyCode <= 90) {
          let index = keyCode - 65;

          if (index > -1) {
            let option = this.question.options[index];

            if (option) {
              this.toggleAnswer(option);
            } else if (
              this.question.allowOther &&
              index === this.question.options.length
            ) {
              this.startEditOther();
            }
          }
        }
      }
    },

    getLabel(index) {
      return this.language.ariaMultipleChoice.replace(
        ":letter",
        this.getToggleKey(index)
      );
    },

    getToggleKey(index) {
      const key = 65 + index;

      if (key <= 90) {
        return String.fromCharCode(key);
      }

      return "";
    },

    toggleAnswer(option) {
      if (this.disableAnswer) {
        return;
      }

      if (this.question.answerOptions !== 'multiple') {
        if (this.question.allowOther) {
          this.question.other = this.dataValue = null;
          this.setAnswer(this.dataValue);
        }

        for (let i = 0; i < this.question.options.length; i++) {
          let o = this.question.options[i];

          if (o.selected) {
            this._toggleAnswer(o);
          }
        }
      }

      this._toggleAnswer(option);
    },

    _toggleAnswer(option) {
      option.toggle();

      if (this.question.answerOptions === 'multiple') {
        this.enterPressed = false;

        if (!option.selected) {
          this._removeAnswer(option.choiceValue());
        } else {
          this.dataValue.push(option.choiceValue());
        }
      } else {
        this.dataValue = option.selected ? option.choiceValue() : null;
      }

      this.setAnswer(this.dataValue);

      if (
        this.isValid() &&
        this.question.nextStepOnAnswer &&
        !this.question.multiple
      ) {
        setTimeout(() => this.$emit("next"), 350);
      }
    },

    _removeAnswer(value) {
      const index = this.dataValue.indexOf(value);

      if (index !== -1) {
        this.dataValue.splice(index, 1);
      }
    },

    startEditOther() {
      this.editingOther = true;
      this.enterPressed = false;

      this.$nextTick(() => {
        this.$refs.otherInput.focus();
      });
    },

    onChangeOther() {
      if (this.editingOther) {
        let value = [],
          self = this;

        this.question.options.forEach(function(option) {
          if (option.selected) {
            if (self.question.multiple) {
              value.push(option.choiceValue());
            } else {
              option.toggle();
            }
          }
        });

        if (this.question.other && this.question.multiple) {
          value.push(this.question.other);
        } else if (!this.question.multiple) {
          value = this.question.other;
        }

        this.dataValue = value;
        this.setAnswer(this.dataValue);
      }
    },

    stopEditOther() {
      this.editingOther = false;
    },

    buildImageLink(key) {
      return `https://${bucketName}.s3.${region}.amazonaws.com/${key}`;
    },
  },

  props: {
    disableAnswer: {
      type: Boolean,
      default: false,
    }
  }
};
</script>

<style scoped>
.option-wrapper {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: row;
  flex-wrap: wrap;
}

.choice-option {
  display: flex;
  flex-direction: column;
  width: 135px;
  height: 154px;
  margin-right: 8px;
  cursor: pointer;
  padding: 12px 12px;
  border-radius: 8px;
}

.choice-option:nth-child(1) {
  background: #166ED8;
}
.choice-option:nth-child(2) {
  background: #FFA726;
}
.choice-option:nth-child(3) {
  background: #F7195A;
}
.choice-option:nth-child(4) {
  background: #4C3E8C;
}

.choice-wrapper:last-child {
  margin-right: 0px;
}

.bullet {
  min-width: 16px;
  width: 16px;
  height: 16px;
  background-color: white;
}

.choice-label {
  margin: auto;
  text-align: center;
}

.choice-image {
  border: 1px solid white;
  max-width: 100%;
  height: 90%;
  margin: auto;
  margin-top: 4px;
  z-index: 20;
}

.choice-image img {
  margin-bottom: 0px;
  height: 100%;
  width: 100%;
}

.label {
  width: 656px;
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.mobile-list {
  display: flex;
  align-items: center;
}

.mb-1 {
  margin-bottom: 8px;
}

@media screen and (max-width: 420px) {
  .choice-option {
    width: 120px;
    margin: 2px;
  }
}

@media screen and (max-width: 768px) {
  .choice-option {
    width: 125px;
    margin: 2px;
  }
}

/* .f-selected .f-key .bullet {
  background-color: white;
}

.f-selected .f-key {
  border: 1px solid white;
}

.f-key {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  float: left;
  border-radius: 8px;
} */
</style>
